/**
 * Select dropdown to choose artifact version
 * Similar to ../Launch/SelectJobVersion
 */
// @todo: Migrate to 'Select' from @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import {Select} from '@wandb/weave/components/Form/Select';
// @todo: Migrate to 'AliasGroup' from @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import {Alias} from '@wandb/weave/components/Tag';
import React from 'react';

import {
  findVersionNumber,
  RE_VERSION,
  sortAliases,
} from '../../../../util/artifacts';
import * as S from './SelectArtifactCollectionVersion.styles';

export type ArtifactVersion = {
  versionId: string;
  versionIndex: number;
  aliases: string[];
  ttlDurationSeconds?: number;
};

// Option for an alias/version
export type ArtifactVersionOption = {
  label: string;
  value: string;
  // This should be set for everything other than "All".
  artifactVersion: ArtifactVersion;
};

// Option for group separator in select
export type ArtifactVersionGroupOption = {
  label: string;
  options: ArtifactVersionOption[];
};

export type ArtifactVersionOptions = Array<
  ArtifactVersionOption | ArtifactVersionGroupOption
>;

type SelectArtifactCollectionVersionProps = {
  artifactVersions: ArtifactVersion[];
  includeAll?: boolean;
  selectedAlias: string;
  disabled?: boolean;
  onSelectArtifactAlias?: (option: ArtifactVersionOption) => void;
};

// Convert ArtifactVersions into nested select widget options
// and a mapping from alias string to option.
const getOptionsFromArtifactVersions = (
  artifactVersions: ArtifactVersion[],
  includeAll: boolean
) => {
  const optionMap: Record<string, ArtifactVersionOption> = {};

  // This will be used to display the version number next to each alias.
  const mapAliasToArtifactVersions: Record<string, ArtifactVersion> = {};
  const mapVersionIndexToArtifactVersions: Record<string, ArtifactVersion> = {};

  // Separate out version and non-version aliases.
  const aliases: string[] = [];
  const versions: number[] = [];
  const emptyArtifactVersion: ArtifactVersion = {
    versionId: '',
    versionIndex: -1,
    aliases: [],
  };
  for (const version of artifactVersions) {
    const versionNumber = findVersionNumber(version.aliases);
    mapVersionIndexToArtifactVersions[versionNumber] = version;
    for (const alias of version.aliases) {
      mapAliasToArtifactVersions[alias] = version;
      const match = alias.match(RE_VERSION);
      if (match) {
        versions.push(parseInt(match[1], 10));
      } else {
        aliases.push(alias);
      }
    }
  }

  // "latest" is special, sort it first.
  const sortedAliases = sortAliases(aliases);
  const aliasOptions = sortedAliases.map(a => ({
    value: a,
    label: a,
    artifactVersion: mapAliasToArtifactVersions[a],
  }));
  aliasOptions.forEach(opt => (optionMap[opt.value] = opt));

  // Sort version numbers in descending order
  const sortedVersions = versions.sort((a, b) => b - a);
  const versionOptions = sortedVersions.map(v => ({
    value: `v${v}`,
    label: `v${v}`,
    artifactVersion: mapVersionIndexToArtifactVersions[v],
  }));
  versionOptions.forEach(opt => (optionMap[opt.value] = opt));

  const options: ArtifactVersionOptions = [
    {
      label: 'Aliases',
      options: aliasOptions,
    },
    {
      label: 'Versions',
      options: versionOptions,
    },
  ];
  if (includeAll) {
    const allOption = {
      value: '*',
      label: 'All versions',
      artifactVersion: emptyArtifactVersion,
    };
    options.unshift(allOption);
    optionMap['*'] = allOption;
  }
  return {options, optionMap};
};

const formatOptionLabel = ({
  value,
  label,
  artifactVersion,
}: ArtifactVersionOption) => {
  if (value === '*') {
    return 'All versions';
  }
  if (value.match(RE_VERSION)) {
    return label;
  }
  return (
    <S.AliasOption>
      <S.Alias>
        <Alias label={value} color="sienna" showIcon={true} />
      </S.Alias>
      <S.AliasVersion>v{artifactVersion.versionIndex}</S.AliasVersion>
    </S.AliasOption>
  );
};

export const SelectArtifactCollectionVersion = ({
  artifactVersions,
  selectedAlias,
  onSelectArtifactAlias,
  includeAll,
  disabled,
}: SelectArtifactCollectionVersionProps) => {
  const {options, optionMap} = getOptionsFromArtifactVersions(
    artifactVersions,
    includeAll ?? false
  );

  const onChange = (option: ArtifactVersionOption | null) => {
    if (option) {
      if (onSelectArtifactAlias) {
        onSelectArtifactAlias(option);
      }
      selectedAlias = option.value;
    }
  };
  const value = optionMap[selectedAlias];
  return (
    <Select
      options={options}
      value={value}
      onChange={onChange}
      formatOptionLabel={formatOptionLabel}
      isDisabled={disabled}
      placeholder="Select an artifact version"
    />
  );
};
